
import React, { useState, useEffect } from 'react';
import { useAppContext } from '../App';
import { Video, Users, Church, Shield, User, BookOpen, Layout, MapPin, HandHeart, UserCircle, Edit3, CreditCard, Heart, Settings, AlertTriangle, List, CheckCircle, Search, Calendar, Image as ImageIcon } from 'lucide-react';

// --- Mock Components for Slideshow Visuals ---

const MockFeed = () => (
    <div className="bg-white w-full h-full p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col gap-3">
        <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full" />
            <div className="h-2 w-24 bg-gray-200 rounded" />
        </div>
        <div className="h-20 bg-gray-50 rounded border border-dashed border-gray-200 flex items-center justify-center text-gray-400 text-xs">
            Post Content Area
        </div>
        <div className="flex gap-2 mt-auto">
            <div className="h-6 w-6 bg-gray-100 rounded" />
            <div className="h-6 w-6 bg-gray-100 rounded" />
            <div className="h-6 w-20 bg-blue-600 rounded ml-auto" />
        </div>
    </div>
);

const MockEvents = () => (
    <div className="bg-white w-full h-full p-2 rounded-lg shadow-sm border border-gray-100 flex gap-2">
        <div className="w-1/3 bg-gray-100 rounded h-full" />
        <div className="flex-1 flex flex-col gap-2">
            <div className="h-3 w-3/4 bg-gray-200 rounded" />
            <div className="h-2 w-1/2 bg-gray-100 rounded" />
            <div className="mt-auto h-6 w-full bg-blue-100 rounded text-blue-600 text-[8px] flex items-center justify-center font-bold">RSVP</div>
        </div>
    </div>
);

const MockVolunteering = () => (
    <div className="bg-white w-full h-full p-3 rounded-lg shadow-sm border border-orange-200 border-l-4">
        <div className="flex justify-between mb-2">
            <div className="h-3 w-1/2 bg-gray-200 rounded" />
            <div className="h-3 w-10 bg-orange-100 rounded" />
        </div>
        <div className="h-2 w-full bg-gray-100 rounded mb-1" />
        <div className="h-2 w-2/3 bg-gray-100 rounded" />
    </div>
);

const MockSermon = () => (
    <div className="bg-white w-full h-full p-2 rounded-lg shadow-sm border border-gray-100 flex flex-col">
        <div className="flex gap-1 mb-2 border-b pb-1">
            <div className="w-4 h-4 bg-gray-200 rounded" />
            <div className="w-4 h-4 bg-gray-200 rounded" />
            <div className="w-4 h-4 bg-gray-200 rounded" />
        </div>
        <div className="flex-1 bg-gray-50 rounded p-2 text-[8px] text-gray-400 leading-relaxed">
            Start typing sermon notes...
        </div>
    </div>
);

const MockDonation = () => (
    <div className="bg-white w-full h-full p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center justify-center gap-2">
        <div className="h-8 w-full bg-gray-100 rounded border border-gray-200" />
        <div className="flex gap-2 w-full">
            <div className="h-8 w-1/2 bg-gray-100 rounded" />
            <div className="h-8 w-1/2 bg-gray-100 rounded" />
        </div>
        <div className="h-8 w-full bg-green-600 rounded text-white text-xs flex items-center justify-center font-bold">Give Securely</div>
    </div>
);

const MockAdmin = () => (
    <div className="bg-white w-full h-full p-2 rounded-lg shadow-sm border border-gray-100">
        <div className="flex justify-between items-center mb-2 pb-1 border-b">
            <div className="h-3 w-20 bg-gray-200 rounded" />
            <div className="h-3 w-10 bg-gray-100 rounded" />
        </div>
        {[1,2,3].map(i => (
             <div key={i} className="flex justify-between items-center mb-1 bg-gray-50 p-1 rounded">
                <div className="flex gap-1 items-center">
                    <div className="w-4 h-4 bg-gray-200 rounded-full" />
                    <div className="h-2 w-10 bg-gray-200 rounded" />
                </div>
                <div className="w-3 h-3 bg-red-100 rounded" />
             </div>
        ))}
    </div>
);

const FeatureSlideshow: React.FC = () => {
    const { t } = useAppContext();
    const [currentRoleIndex, setCurrentRoleIndex] = useState(0);

    const roles = [
        {
            id: 'member',
            title: t('guide_role_member'),
            icon: User,
            color: 'bg-blue-600',
            features: [
                { title: t('guide_feat_feed'), desc: t('guide_feat_feed_desc'), icon: Layout, mockup: <MockFeed /> },
                { title: t('guide_feat_events'), desc: t('guide_feat_events_desc'), icon: MapPin, mockup: <MockEvents /> },
                { title: t('guide_feat_volunteering'), desc: t('guide_feat_volunteering_desc'), icon: HandHeart, mockup: <MockVolunteering /> },
                { title: t('guide_feat_profile'), desc: t('guide_feat_profile_desc'), icon: UserCircle, mockup: <div className="bg-gray-100 w-full h-full rounded-lg flex items-center justify-center"><UserCircle className="w-12 h-12 text-gray-300"/></div> }
            ]
        },
        {
            id: 'pastor',
            title: t('guide_role_pastor'),
            icon: Users,
            color: 'bg-purple-600',
            features: [
                { title: t('guide_feat_sermon'), desc: t('guide_feat_sermon_desc'), icon: Edit3, mockup: <MockSermon /> },
                { title: t('guide_feat_events_church'), desc: t('guide_feat_events_church_desc'), icon: Calendar, mockup: <MockEvents /> },
                { title: t('guide_feat_team'), desc: t('guide_feat_team_desc'), icon: Users, mockup: <div className="bg-white p-2 rounded w-full h-full border"><div className="bg-purple-50 p-1 rounded mb-1 text-[8px] text-purple-700 font-bold">Admin Team</div><div className="space-y-1">{[1,2].map(i=><div key={i} className="h-4 bg-gray-50 rounded flex items-center px-1"><div className="w-2 h-2 bg-gray-300 rounded-full mr-1"/></div>)}</div></div> }
            ]
        },
        {
            id: 'church',
            title: t('guide_role_church'),
            icon: Church,
            color: 'bg-green-600',
            features: [
                { title: t('guide_feat_registration'), desc: t('guide_feat_registration_desc'), icon: CheckCircle, mockup: <div className="bg-white p-3 w-full h-full rounded border flex flex-col items-center justify-center gap-2"><div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center"><CheckCircle className="w-5 h-5 text-green-600"/></div><div className="text-[8px] text-gray-500">Verified Page</div></div> },
                { title: t('guide_feat_donations'), desc: t('guide_feat_donations_desc'), icon: CreditCard, mockup: <MockDonation /> },
                { title: t('guide_feat_causes'), desc: t('guide_feat_causes_desc'), icon: Heart, mockup: <MockVolunteering /> }
            ]
        },
        {
            id: 'admin',
            title: t('guide_role_admin'),
            icon: Shield,
            color: 'bg-red-600',
            features: [
                { title: t('guide_feat_user_mgmt'), desc: t('guide_feat_user_mgmt_desc'), icon: List, mockup: <MockAdmin /> },
                { title: t('guide_feat_safety'), desc: t('guide_feat_safety_desc'), icon: AlertTriangle, mockup: <div className="w-full h-full bg-red-50 rounded border border-red-100 flex items-center justify-center flex-col gap-1"><AlertTriangle className="w-6 h-6 text-red-500"/><span className="text-[8px] font-bold text-red-700">Flagged Content</span></div> },
                { title: t('guide_feat_settings'), desc: t('guide_feat_settings_desc'), icon: Settings, mockup: <div className="w-full h-full bg-white rounded border p-2"><div className="h-2 w-1/2 bg-gray-200 rounded mb-2"/><div className="h-4 w-full bg-gray-100 rounded mb-1"/><div className="h-4 w-full bg-gray-100 rounded mb-1"/></div> }
            ]
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentRoleIndex((prev) => (prev + 1) % roles.length);
        }, 5000); // Change role slide every 5 seconds
        return () => clearInterval(timer);
    }, []);

    const currentRole = roles[currentRoleIndex];

    return (
        <div className="w-full aspect-video bg-gray-50 rounded-xl overflow-hidden shadow-lg relative border border-gray-200">
            {/* Header / Role Indicator */}
            <div className={`${currentRole.color} text-white p-4 flex items-center justify-between transition-colors duration-500`}>
                <div className="flex items-center gap-3">
                    <currentRole.icon className="w-6 h-6" />
                    <h2 className="text-xl font-bold">{currentRole.title} Features</h2>
                </div>
                <div className="flex gap-1">
                    {roles.map((r, idx) => (
                        <div 
                            key={r.id} 
                            onClick={() => setCurrentRoleIndex(idx)}
                            className={`h-2 w-2 rounded-full cursor-pointer transition-all ${idx === currentRoleIndex ? 'bg-white w-6' : 'bg-white/40'}`}
                        />
                    ))}
                </div>
            </div>

            {/* Content Area - Animated Grid of Features */}
            <div className="p-6 h-[calc(100%-4rem)] overflow-hidden relative">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
                    {currentRole.features.map((feat, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 flex gap-3 items-start animate-fadeIn" style={{ animationDelay: `${idx * 100}ms` }}>
                            {/* Feature Description */}
                            <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-1">
                                    <feat.icon className="w-4 h-4 text-gray-500" /> {feat.title}
                                </h4>
                                <p className="text-xs text-gray-500 leading-relaxed">{feat.desc}</p>
                            </div>
                            
                            {/* Visual Mockup "Screenshot" */}
                            <div className="w-24 h-20 shrink-0 transform rotate-1 hover:rotate-0 transition-transform duration-300">
                                {feat.mockup}
                            </div>
                        </div>
                    ))}
                </div>
                
                {/* Decoration */}
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-gradient-to-br from-gray-100 to-transparent rounded-full opacity-50 pointer-events-none" />
            </div>
        </div>
    );
};

const UserGuide: React.FC = () => {
    const { t } = useAppContext();
    const [activeTab, setActiveTab] = useState<'member' | 'pastor' | 'church' | 'admin'>('member');

    const renderContent = () => {
        switch(activeTab) {
            case 'member':
                return (
                    <div className="space-y-4 animate-fadeIn">
                        <div className="flex items-center gap-3 mb-2">
                            <User className="w-8 h-8 text-blue-600" />
                            <h3 className="text-xl font-bold">{t('guide_role_member')}</h3>
                        </div>
                        <p className="text-gray-700 leading-relaxed text-lg">{t('guide_member_desc')}</p>
                        <ul className="list-disc pl-5 space-y-2 text-gray-600">
                            <li><strong>{t('guide_feat_feed')}:</strong> {t('guide_feat_feed_desc')}</li>
                            <li><strong>{t('guide_feat_events')}:</strong> {t('guide_feat_events_desc')}</li>
                            <li><strong>{t('guide_feat_volunteering')}:</strong> {t('guide_feat_volunteering_desc')}</li>
                            <li><strong>{t('guide_feat_profile')}:</strong> {t('guide_feat_profile_desc')}</li>
                        </ul>
                    </div>
                );
            case 'pastor':
                return (
                    <div className="space-y-4 animate-fadeIn">
                        <div className="flex items-center gap-3 mb-2">
                            <Users className="w-8 h-8 text-purple-600" />
                            <h3 className="text-xl font-bold">{t('guide_role_pastor')}</h3>
                        </div>
                        <p className="text-gray-700 leading-relaxed text-lg">{t('guide_pastor_desc')}</p>
                        <ul className="list-disc pl-5 space-y-2 text-gray-600">
                            <li><strong>{t('guide_feat_sermon')}:</strong> {t('guide_feat_sermon_desc')}</li>
                            <li><strong>{t('guide_feat_events_church')}:</strong> {t('guide_feat_events_church_desc')}</li>
                            <li><strong>{t('guide_feat_team')}:</strong> {t('guide_feat_team_desc')}</li>
                        </ul>
                    </div>
                );
            case 'church':
                return (
                    <div className="space-y-4 animate-fadeIn">
                        <div className="flex items-center gap-3 mb-2">
                            <Church className="w-8 h-8 text-green-600" />
                            <h3 className="text-xl font-bold">{t('guide_role_church')}</h3>
                        </div>
                        <p className="text-gray-700 leading-relaxed text-lg">{t('guide_church_desc')}</p>
                        <ul className="list-disc pl-5 space-y-2 text-gray-600">
                            <li><strong>{t('guide_feat_registration')}:</strong> {t('guide_feat_registration_desc')}</li>
                            <li><strong>{t('guide_feat_donations')}:</strong> {t('guide_feat_donations_desc')}</li>
                            <li><strong>{t('guide_feat_causes')}:</strong> {t('guide_feat_causes_desc')}</li>
                        </ul>
                    </div>
                );
            case 'admin':
                return (
                     <div className="space-y-4 animate-fadeIn">
                        <div className="flex items-center gap-3 mb-2">
                            <Shield className="w-8 h-8 text-red-600" />
                            <h3 className="text-xl font-bold">{t('guide_role_admin')}</h3>
                        </div>
                        <p className="text-gray-700 leading-relaxed text-lg">{t('guide_admin_desc')}</p>
                        <ul className="list-disc pl-5 space-y-2 text-gray-600">
                            <li><strong>{t('guide_feat_user_mgmt')}:</strong> {t('guide_feat_user_mgmt_desc')}</li>
                            <li><strong>{t('guide_feat_safety')}:</strong> {t('guide_feat_safety_desc')}</li>
                            <li><strong>{t('guide_feat_settings')}:</strong> {t('guide_feat_settings_desc')}</li>
                        </ul>
                    </div>
                );
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 pb-12">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-8 text-white shadow-lg">
                <div className="flex items-start gap-4">
                    <BookOpen className="w-12 h-12 text-blue-200 mt-1" />
                    <div>
                        <h1 className="text-3xl font-bold">{t('guide_title')}</h1>
                        <p className="text-blue-100 text-lg mt-2">{t('guide_subtitle')}</p>
                    </div>
                </div>
            </div>

            {/* Animated Feature Slideshow */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-col md:flex-row gap-6 items-center">
                    <div className="flex-1 space-y-4">
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <Video className="text-blue-600" /> {t('guide_video_tour')}
                        </h2>
                        <p className="text-gray-600 leading-relaxed">
                            {t('guide_video_desc')}
                        </p>
                    </div>
                    
                    {/* Slideshow Area */}
                    <div className="w-full md:w-1/2">
                        <FeatureSlideshow />
                    </div>
                </div>
            </div>

            {/* Role Based Text Guide */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex border-b border-gray-200 overflow-x-auto">
                    <button onClick={() => setActiveTab('member')} className={`px-6 py-4 font-medium transition whitespace-nowrap ${activeTab === 'member' ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}>
                        {t('guide_role_member')}
                    </button>
                    <button onClick={() => setActiveTab('pastor')} className={`px-6 py-4 font-medium transition whitespace-nowrap ${activeTab === 'pastor' ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600' : 'text-gray-500 hover:bg-gray-50'}`}>
                        {t('guide_role_pastor')}
                    </button>
                    <button onClick={() => setActiveTab('church')} className={`px-6 py-4 font-medium transition whitespace-nowrap ${activeTab === 'church' ? 'bg-green-50 text-green-700 border-b-2 border-green-600' : 'text-gray-500 hover:bg-gray-50'}`}>
                        {t('guide_role_church')}
                    </button>
                    <button onClick={() => setActiveTab('admin')} className={`px-6 py-4 font-medium transition whitespace-nowrap ${activeTab === 'admin' ? 'bg-red-50 text-red-700 border-b-2 border-red-600' : 'text-gray-500 hover:bg-gray-50'}`}>
                        {t('guide_role_admin')}
                    </button>
                </div>
                <div className="p-8 min-h-[300px]">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default UserGuide;
